
import React, { useMemo, useState, useEffect } from 'react';

const ModalFaturamento = ({ show, onClose, onConfirm, loading = false }) => {
  const [opcao, setOpcao] = useState('atual'); // 'atual' | 'proximo'

  // fecha com ESC (se não estiver carregando)
  useEffect(() => {
    if (!show) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && !loading) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [show, loading, onClose]);

  const infoMes = useMemo(() => {
    const now = new Date();
    const base = new Date(now.getFullYear(), now.getMonth(), 1);

    let offsetMeses = 0; // O número de meses a adicionar à data base.

    if (opcao === 'proximo') {
      offsetMeses = 1;
    } else if (opcao === 'proximo2') {
      offsetMeses = 2; // Adiciona 2 meses quando opcao é 'proximo2'
    }

    // A data de referência é baseada no mês atual + o offset calculado
    const ref = new Date(base.getFullYear(), base.getMonth() + offsetMeses, 1);

    const ano = ref.getFullYear();
    const mes = ref.getMonth() + 1; // 1..12
    const mesStr = String(mes).padStart(2, '0');
    const label = ref.toLocaleString('pt-BR', { month: 'long', year: 'numeric' });

    return { ano, mes, label, yyyymm: `${ano}-${mesStr}` };
  }, [opcao]);

  if (!show) return null;

  return (
    <div
      style={styles.backdrop}
      onClick={() => { if (!loading) onClose?.(); }}
      aria-hidden={loading ? 'true' : 'false'}
    >
      <div
        style={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="fat-title"
        aria-busy={loading}
      >
        <h3 id="fat-title" style={styles.title}>Gerar Faturamento</h3>

        <div style={{ display: 'grid', gap: 12 }}>
          <label style={styles.label}>Mês para faturamento</label>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setOpcao('atual')}
              style={opcao === 'atual' ? styles.chipActive : styles.chip}
              disabled={loading}
            >
              Mês atual
            </button>
            <button
              type="button"
              onClick={() => setOpcao('proximo')}
              style={opcao === 'proximo' ? styles.chipActive : styles.chip}
              disabled={loading}
            >
              Próximo mês
            </button>
            <button
              type="button"
              onClick={() => setOpcao('proximo2')}
              style={opcao === 'proximo2' ? styles.chipActive : styles.chip}
              disabled={loading}
            >
              2 mês a frente
            </button>
          </div>

          <div style={styles.previewBox}>
            <span>Selecionado: </span>
            <strong style={{ textTransform: 'capitalize' }}>{infoMes.label}</strong>
            <span style={{ color: '#6b7280', marginLeft: 8 }}>({infoMes.yyyymm})</span>
          </div>
        </div>

        <div style={styles.actions}>
          <button
            style={{ ...styles.btnLight, opacity: loading ? 0.6 : 1 }}
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>

          <button
            style={{ ...styles.btnPrimary, opacity: loading ? 0.85 : 1, position: 'relative', minWidth: 120 }}
            onClick={() => onConfirm?.(infoMes)}
            disabled={loading}
          >
            {loading ? (
              <div style={styles.btnContent}>
                <Spinner />
                <span style={{ marginLeft: 8 }}>Processando...</span>
              </div>
            ) : (
              'Confirmar'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const Spinner = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 50 50"
    aria-hidden="true"
    focusable="false"
    style={{ display: 'block' }}
  >
    <circle
      cx="25"
      cy="25"
      r="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="5"
      strokeOpacity="0.2"
    />
    <path
      fill="none"
      stroke="currentColor"
      strokeWidth="5"
      d="M25 5 a20 20 0 0 1 0 40"
    >
      <animateTransform
        attributeName="transform"
        type="rotate"
        from="0 25 25"
        to="360 25 25"
        dur="0.8s"
        repeatCount="indefinite"
      />
    </path>
  </svg>
);

const styles = {
  backdrop: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50
  },
  modal: {
    width: '100%', maxWidth: 420, background: '#fff', borderRadius: 12,
    padding: 16, boxShadow: '0 10px 25px rgba(0,0,0,0.15)', border: '1px solid #e5e7eb'
  },
  title: { margin: 0, marginBottom: 12, fontSize: '1.1rem' },
  label: { fontSize: 14, fontWeight: 500 },
  chip: {
    padding: '6px 10px', borderRadius: 999, border: '1px solid #e5e7eb',
    background: '#fff', cursor: 'pointer'
  },
  chipActive: {
    padding: '6px 10px', borderRadius: 999, border: '1px solid #667eea',
    background: '#667eea', color: '#fff', cursor: 'pointer'
  },
  previewBox: {
    marginTop: 4, padding: '8px 10px', border: '1px solid #e5e7eb',
    borderRadius: 8, background: '#f9fafb', fontSize: 14
  },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 },
  btnLight: { background: '#e5e7eb', color: '#111827', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer' },
  btnPrimary: { background: '#667eea', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer' },
  btnContent: { display: 'flex', alignItems: 'center', justifyContent: 'center' }
};

export default ModalFaturamento;
